import React from 'react';
import { motion } from 'framer-motion';

import Icon from '../../../components/AppIcon';

const CompanyStory = () => {
  const storyMilestones = [
    {
      year: "2015",
      title: "Fondation & Vision",
      description: "ORAZEK Investment a été fondée avec une mission claire : fournir des solutions d'affaires complètes et fiables en République Démocratique du Congo. En commençant par les fournitures de bureau et les services de base, nous avons identifié le manque sur le marché d'un fournisseur multi-services fiable capable de répondre aux divers besoins commerciaux avec une qualité constante.",
      icon: "Rocket"
    },
    {
      year: "2017",
      title: "Expansion des Services",
      description: "En réponse à la demande des clients, nous nous sommes étendus à l'immobilier, la gestion de flottes de véhicules et les services informatiques. Cette diversification stratégique nous a permis de devenir un véritable fournisseur de solutions intégrées, offrant des services qui simplifient les opérations commerciales pour notre clientèle croissante.",
      icon: "TrendingUp"
    },
    {
      year: "2019",
      title: "Partenariats Corporatifs",
      description: "Établissement de partenariats stratégiques avec des entreprises internationales et des entités gouvernementales. Notre engagement envers la qualité et la conformité nous a valu la reconnaissance en tant que fournisseur de services privilégié pour les organisations nécessitant des partenariats locaux fiables avec des normes internationales.",
      icon: "Handshake"
    },
    {
      year: "2021",
      title: "Transformation Numérique",
      description: "Lancement de plateformes numériques complètes pour améliorer la prestation de services et l'expérience client. Mise en œuvre de systèmes de réservation avancés, de suivi en temps réel et de solutions de gestion intégrées qui ont positionné ORAZEK à l'avant-garde de l'innovation commerciale en RDC.",
      icon: "Laptop"
    },
    {
      year: "2023",
      title: "Leadership du Marché",
      description: "Reconnaissance en tant que principal fournisseur de solutions complètes en RDC, servant plus de 500 clients dans plusieurs secteurs. Expansion de l'offre de services à 12 secteurs distincts tout en maintenant notre engagement envers l'excellence et la satisfaction client.",
      icon: "Award"
    },
    {
      year: "2026",
      title: "Vision d\'Avenir",
      description: "Poursuivant notre parcours d'innovation et d'excellence, nous étendons notre portée à travers l'Afrique centrale tout en approfondissant notre expertise dans les technologies émergentes. Notre vision reste inchangée : être le partenaire le plus fiable pour les entreprises recherchant des solutions complètes et fiables.",
      icon: "Target"
    }
  ];

  return (
    <section className="relative overflow-hidden py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 border-y border-slate-200 dark:border-white/5">
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-6 py-3 rounded-full mb-6">
            <Icon name="BookOpen" size={20} className="text-amber-400" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Notre Parcours</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-4">
            Une Décennie d'<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">Excellence</span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
            De débuts modestes au leadership du marché, notre histoire est celle d'un engagement inébranlable envers la qualité, l'innovation et le succès des clients en République Démocratique du Congo.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-600 via-amber-500 to-blue-600"></div>

          <div className="space-y-8 md:space-y-12 lg:space-y-16">
            {storyMilestones?.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-6 lg:p-8 hover:border-blue-500/30 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0">
                        <Icon name={milestone?.icon} size={20} className="text-white" />
                      </div>
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="text-2xl lg:text-3xl font-bold text-amber-400">
                          {milestone?.year}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white mb-3">
                      {milestone?.title}
                    </h3>
                    <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                      {milestone?.description}
                    </p>
                  </div>
                </div>

                {/* Timeline dot */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full bg-amber-500 border-4 border-slate-100 dark:border-slate-900 shadow-lg shadow-amber-500/20"></div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyStory;
